import { useState } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components';

interface GuidedTourProps {
    open: boolean;
    onClose: () => void;
}

const tourSteps = [
    {
        title: 'Welcome to Bias Detection Tool',
        description:
            'Welcome to our advanced linguistic analysis tool designed to identify potential biases in written content.',
    },
    {
        title: 'Input Text',
        description:
            'Enter or import your text in the designated area. Please ensure the content adheres to the specified length limitations.',
    },
    {
        title: 'Initiate Analysis',
        description: 'Click on "Detect Bias" to begin the analytical process.',
    },
    {
        title: 'Review Analysis',
        description:
            'Examine the highlighted passages to identify potential instances of bias in your content.',
    },
    {
        title: 'Configure Settings',
        description:
            'Fine-tune the detection sensitivity and filtering options to optimize analysis accuracy.',
    },
    {
        title: 'Explanation Generation',
        description:
            'Click on "Explain Bias Analysis" to generate detailed explanations for the detected biases.',
    },
    {
        title: 'Explanation Insights',
        description:
            'Access detailed explanations and visualizations to gain deeper insights into the detected biases.',
    },
    {
        title: 'Export Results',
        description:
            'Download the analysis results for further review and documentation.',
    },
    {
        title: 'View History',
        description:
            'Access previous analysis sessions and review detailed insights for each session.',
    },
    {
        title: 'Analysis Dashboard',
        description:
            'Explore comprehensive data visualizations for detailed analytical insights.',
    },
];

export function GuidedTour({ open, onClose }: GuidedTourProps) {
    const [currentStep, setCurrentStep] = useState(0);

    const handleNext = () => {
        if (currentStep < tourSteps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onClose();
            setCurrentStep(0);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{tourSteps[currentStep].title}</DialogTitle>
                    <DialogDescription>
                        {tourSteps[currentStep].description}
                    </DialogDescription>
                </DialogHeader>
                <div className="mt-4 flex justify-between">
                    <Button
                        variant="outline"
                        onClick={handlePrevious}
                        disabled={currentStep === 0}
                    >
                        Previous
                    </Button>
                    <Button onClick={handleNext}>
                        {currentStep === tourSteps.length - 1
                            ? 'Finish'
                            : 'Next'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
